// src/pages/CookiesPolicy.jsx
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

const CookiesPolicy = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const backTo = location.state?.from || "/";

  const LAST_UPDATED = "January 12, 2026";
  const APP_NAME = "PIAS";

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-6 flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">
              Cookies Policy
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Last updated: {LAST_UPDATED}
            </p>
          </div>

          <button
            onClick={() => navigate(backTo)}
            className="text-sm font-medium text-green-600 hover:text-green-700"
          >
            Back
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-sm border p-6 sm:p-8">
          <p className="text-gray-700 leading-relaxed">
            This Cookies Policy describes the use of cookies on{" "}
            <strong>{APP_NAME}</strong>. The Application uses{" "}
            <strong>only strictly necessary technical cookies</strong> for
            login functionality and security.
          </p>

          <Section title="1. What are cookies?">
            <p className="text-gray-700 leading-relaxed">
              Cookies are small files that a website stores on your device to
              enable basic functions, improve security, and remember
              preferences. This Application uses only technical cookies.
            </p>
          </Section>

          <Section title="2. Technical cookies used">
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-50">
                  <tr>
                    <Th>Cookie</Th>
                    <Th>Type</Th>
                    <Th>Purpose</Th>
                    <Th>Approx. duration</Th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  <tr className="border-t">
                    <Td><code className="text-sm">pias_refresh</code></Td>
                    <Td>Technical (HttpOnly)</Td>
                    <Td>
                      Keeps the session active and allows secure access renewal
                      through a refresh token.
                    </Td>
                    <Td>Up to 30 days (configurable)</Td>
                  </tr>
                  <tr className="border-t">
                    <Td><code className="text-sm">pias_csrf</code></Td>
                    <Td>Technical (security)</Td>
                    <Td>Helps mitigate CSRF attacks (forged requests).</Td>
                    <Td>Session / configurable</Td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Section>

          <Section title="3. Consent">
            <p className="text-gray-700 leading-relaxed">
              Since only strictly necessary technical cookies are used, no
              specific consent is requested for their use. These cookies are not
              used for advertising or tracking purposes.
            </p>
          </Section>

          <Section title="4. How to manage cookies">
            <p className="text-gray-700 leading-relaxed">
              You can manage or delete cookies through your browser settings.
              Please note that disabling technical cookies may affect login and
              the proper functioning of the Application.
            </p>
          </Section>
        </div>

        <p className="text-xs text-gray-500 mt-6">
          Note: This is a general informational document. For specific cases,
          seek professional advice.
        </p>
      </main>
    </div>
  );
};

function Section({ title, children }) {
  return (
    <section className="mt-8">
      <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Th({ children }) {
  return (
    <th className="text-left text-xs font-semibold text-gray-600 px-4 py-3 uppercase tracking-wider">
      {children}
    </th>
  );
}

function Td({ children }) {
  return <td className="text-sm text-gray-700 px-4 py-3 align-top">{children}</td>;
}

export default CookiesPolicy;
